import React from 'react'
import _ from 'lodash'
import { Message } from '@paypalcorp/worldready-react'
import { errorConstants } from './errorConstants'
import serverData from '../utils/server-data'
import { constants } from './constants'

const findMessage = (string, keyword) => {
  if (string.includes(keyword)) {
    return true
  }
  return false
}

const formatCoCErrorMessage = (message) => {
  // Split into "message before :" and the dynamic recipients after :
  const [before, after = ''] = message.split(/:(.*)/s).map((s) => s.trim())

  // Normalize "these recipients" -> "below recipients" (case-insensitive)
  const generalMessage =
    before.replace(/these recipients/i, 'below recipients') + ' :'

  // Split recipients on ';', clean up, and normalize "User(s) -" prefix
  const userList = after
    .split(';')
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => s.replace(/^User\(s\)\s*-?\s*/i, 'User(s) - ')) // ensure consistent "User(s) - "

  return {
    generalMessage,
    userList,
  }
}

export function get400ErrorMessage(errorMessage, file, operation) {
  if (findMessage(errorMessage, 'cannot overwrite system file')) {
    return <Message id={`alert.dot.not.allowed.${file.fileType}`} />
  }
  if (findMessage(errorMessage, 'Deleting shared path is not allowed')) {
    return <Message id={`alert.delete.shared.${file.fileType}.without.name`} />
  }
  if (findMessage(errorMessage, 'Please remove contents in the folder first')) {
    return <Message id="error.delete.folder.with.content" />
  }
  if (
    findMessage(
      errorMessage,
      'Changing sharing settings of a sub-folder present in shared parent folder is not supported',
    )
  ) {
    return <Message id={`share.sub.${file.fileType}.of.shared.folder`} />
  }
  if (
    findMessage(
      errorMessage,
      'because it already contains shared folders. Unshare them and try again.',
    )
  ) {
    return <Message id="error.delete.shared.subFileOrFolder" />
  }
  if (findMessage(errorMessage, 'Sharing to self is not allowed')) {
    return <Message id={`share.${file.fileType}.to.self`} />
  }
  if (
    findMessage(
      errorMessage,
      'Operation not allowed. Folder is shared with others',
    )
  ) {
    return <Message id={`alert.${operation}.shared.${file.fileType}`} />
  }
  if (findMessage(errorMessage, 'not exists')) {
    return <Message id="alert.not.exist" />
  }
  if (
    findMessage(
      errorMessage,
      'Sharing is blocked by compliance restrictions',
    ) ||
    findMessage(
      errorMessage,
      'Upload permission is blocked by compliance restrictions',
    )
  ) {
    const msg = formatCoCErrorMessage(errorMessage)
    return msg
  }
  return <Message id="error.invalid.request" />
}

export function get403ErrorMessage(errorMessage, file, operation = '') {
  const isGoPayUser = serverData.user.accType === constants.GoPayUser
  if (findMessage(errorMessage, 'Cannot access system metadata folders')) {
    return <Message id="error.no.permision.system.folder" />
  }
  if (findMessage(errorMessage, 'on shared entity is forbidden')) {
    if (_.has(file, 'fileType') && operation !== '') {
      return (
        <Message
          id={`alert.${operation}.shared.${file.fileType}.without.name`}
        />
      )
    }
    /* Actual error - Operation on shared entity is forbidden 
    Reason for displaying something wrong message - File details and operation details not available 
    Need to display proper error messgage after localizing the error message */
    return <Message id="error.something.wrong" />
  }
  if (findMessage(errorMessage, 'File contains sensitive content')) {
    return isGoPayUser ? (
      <Message id="error.sensitive.gopay.content" />
    ) : (
      <Message id="error.sensitive.content" />
    )
  }
  return <Message id="error.no.permission" />
}

export function get500ErrorMessage(errorMessage, file) {
  if (
    findMessage(
      errorMessage,
      'Malformed input or input contains unmappable characters',
    )
  ) {
    return <Message id="error.malformed.chars" name={file.name} />
  }
  if (findMessage(errorMessage, 'Failed to upload file')) {
    return <Message id="error.upload.failed" />
  }
  return <Message id="error.something.wrong" />
}

export function getErrorMessage(err, file, operation) {
  if (err.status) {
    if (err.status === 401) {
      return ''
    }
    const errorText = JSON.parse(err.response.text)

    if (_.get(errorText, 'clientError', false)) {
      return <Message id={_.get(errorText, 'errorMessage', '')} />
    }
    if (err.status === 400) {
      return get400ErrorMessage(
        _.get(errorText, 'message', ''),
        file,
        operation,
      )
    }
    if (err.status === 403) {
      return get403ErrorMessage(
        _.get(errorText, 'message', ''),
        file,
        operation,
      )
    }
    if (err.status === 500) {
      return get500ErrorMessage(_.get(errorText, 'message', ''), file)
    }
    if (_.has(errorConstants, err.status)) {
      return errorConstants[err.status].message
    }
  }
  return <Message id="error.something.wrong" />
}

export default {
  get400ErrorMessage,
  get403ErrorMessage,
}
